import type { UpdatePlayerLateralitySchema, UpdatePlayerPlayingStyleSchema, UpdatePlayerPreferredPriceSchema, UpdatePlayerPreferredSurfaceSchema } from "@teamz/common";

import AddIcon from "@/assets/add.svg";
import EditIcon from "@/assets/edit.svg";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { playerLaterality, playerPlayingStyles, playerPreferredSurface, updatePlayerLateralitySchema, updatePlayerPlayingStyleSchema, updatePlayerPreferredPriceSchema, updatePlayerPreferredSurfaceSchema } from "@teamz/common";
import { type ComponentProps, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import type { RouterOutputs } from "@/lib/trpc";

import { useMe } from "@/context/user-context";
import { tDyn } from "@/lib/i18n-dynamic";
import { useTRPC } from "@/lib/trpc";
import { cn } from "@/lib/utils";

import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Sheet, SheetClose, SheetContent, SheetFooter, SheetForm, SheetHeader, SheetTitle } from "../ui/sheet";
import { ErrorField, RootField, TextField } from "../ui/text-field";

const positionStyles = {
  "top-left": "rounded-tl-xl border-r border-b",
  "top-right": "rounded-tr-xl border-b",
  "bottom-left": "rounded-bl-xl border-r",
  "bottom-right": "rounded-br-xl",
} as const;

type PlayerSpecs = "playing-style" | "player-laterality" | "preferred-surface" | "preferred-price";

type SpecButtonProps = {
  position: keyof typeof positionStyles;
  data: string | null;
  spec: PlayerSpecs;
};

const SpecButton = ({ ref, position, data, spec, className, ...rest }: ComponentProps<"button"> & SpecButtonProps) => {
  const { t } = useTranslation(["profile"]);
  const { user } = useMe();
  const labels: Record<PlayerSpecs, string> = {
    "playing-style": t("profile:playing-style"),
    "player-laterality": t("profile:laterality"),
    "preferred-surface": t("profile:preferred_surface"),
    "preferred-price": t("profile:preferred_price"),
  };

  return (
    <button
      type="button"
      className={cn(
        "flex h-[104px] w-full flex-col border-neutral-400 px-3 py-4 text-start",
        data ? "bg-white" : "bg-neutral-50",
        positionStyles[position],
        `group data-[color=blue]:bg-blue-500 data-[color=orange]:bg-orange-500`,
        className,
        user.role === "club" && !user.isAdmin && "pointer-events-none",
      )}
      ref={ref}
      {...rest}
    >
      {data && (
        <span className="font-semibold leading-[18px] text-neutral-900 group-data-[color=blue]:text-white group-data-[color=orange]:text-white">
          {data}
        </span>
      )}
      <div className="mt-auto flex w-full items-end justify-between text-neutral-500 group-data-[color=blue]:text-neutral-450 group-data-[color=orange]:text-orange-300">
        <span className=" text-balance text-sm font-normal leading-4">
          {labels[spec]}
        </span>
        { (user.role !== "club" || user.isAdmin) && (
          <div className="shrink-0">
            {data
              ? <EditIcon className="size-5" />
              : <AddIcon className="size-6 text-orange-500" />}
          </div>
        )}
      </div>
    </button>
  );
};

export function PlayerSpecifications({ player }: { player: RouterOutputs["player"]["getPlayer"] }) {
  const [modalState, updateModalState] = useState<PlayerSpecs | "close">("close");
  const { t } = useTranslation(["profile"]);
  const isModalOpen = modalState !== "close";
  const { user } = useMe();

  const labels: Record<PlayerSpecs, string> = {
    "playing-style": t("profile:playing-style"),
    "player-laterality": t("profile:laterality"),
    "preferred-surface": t("profile:preferred_surface"),
    "preferred-price": t("profile:preferred_price"),
  };

  function closeModal() {
    updateModalState("close");
  }

  return (
    <div className="flex flex-col gap-2">
      <h3 className="p-2 font-semibold leading-[1.1] text-neutral-500">{t("profile:specs")}</h3>

      <div className="rounded-[20px] bg-white p-2">
        <div className="grid grid-cols-2 rounded-xl border border-neutral-400">
          <SpecButton
            data={player.playingStyle ? t(`profile:${player.playingStyle}`) : null}
            spec="playing-style"
            position="top-left"
            onClick={() => updateModalState("playing-style")}
          />

          <SpecButton
            spec="player-laterality"
            data={player.laterality ? t(`profile:${player.laterality}`) : null}
            position="top-right"
            onClick={() => updateModalState("player-laterality")}
          />

          <SpecButton
            spec="preferred-surface"
            data={player.preferredSurface ? t(`profile:${player.preferredSurface}`) : null}
            position="bottom-left"
            onClick={() => updateModalState("preferred-surface")}
            data-color={player.preferredSurface ? player.preferredSurface.includes("hard") ? "blue" : "orange" : undefined}
          />

          <SpecButton
            spec="preferred-price"
            data={player.preferredPrice ? `${player.preferredPrice.toLocaleString()} €` : null}
            position="bottom-right"
            onClick={() => updateModalState("preferred-price")}
          />

          {(user.role !== "club" || user.isAdmin) && (
            <Sheet open={isModalOpen} onOpenChange={closeModal}>
              <SheetContent>
                <SheetHeader>
                  <SheetTitle>{isModalOpen && labels[modalState]}</SheetTitle>
                </SheetHeader>

                {modalState === "playing-style" && <PlayerPlayingStyleForm player={player} closeModal={closeModal} />}
                {modalState === "player-laterality" && <PlayerLateralitygForm player={player} closeModal={closeModal} />}
                {modalState === "preferred-surface" && <PlayerPreferredSurfaceForm player={player} closeModal={closeModal} />}
                {modalState === "preferred-price" && <PlayerPreferredPriceForm player={player} closeModal={closeModal} />}
              </SheetContent>
            </Sheet>
          )}
        </div>
      </div>
    </div>
  );
}

function PlayerPlayingStyleForm({ player, closeModal }: { player: RouterOutputs["player"]["getPlayer"]; closeModal: () => void }) {
  const { t } = useTranslation(["common", "profile"]);
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  const { mutate, isPending } = useMutation(trpc.player.updateSpecs.mutationOptions({
    onSuccess: () => {
      queryClient.invalidateQueries(trpc.player.getPlayer.queryFilter());
      closeModal();
    },
  }));

  const playerPlayingStyleForm = useForm<UpdatePlayerPlayingStyleSchema>({
    resolver: zodResolver(updatePlayerPlayingStyleSchema),
    defaultValues: {
      playingStyle: player?.playingStyle || undefined,
    },
  });

  const onSubmit = playerPlayingStyleForm.handleSubmit((data) => {
    mutate({ ...data, userId: player.userId });
  });

  return (
    <>
      <SheetForm id="PlayerPlayingStyleForm" onSubmit={onSubmit}>
        <Controller
          control={playerPlayingStyleForm.control}
          name="playingStyle"
          render={({ field, fieldState }) => (
            <RootField>
              <Label>{t("profile:playing-style")}</Label>
              <Select value={field.value} onValueChange={field.onChange}>
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    {playerPlayingStyles.map(style => (
                      <SelectItem key={style} value={style}>{t(`profile:${style}`)}</SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
              {fieldState.error?.message && (
                <ErrorField>{tDyn(t, fieldState.error?.message)}</ErrorField>
              )}
            </RootField>
          )}
        />
      </SheetForm>

      <SheetFooter>
        <SheetClose asChild>
          <Button variant="dark" type="button">
            {t("common:cancel")}
          </Button>
        </SheetClose>
        <Button type="submit" form="PlayerPlayingStyleForm" loading={isPending || playerPlayingStyleForm.formState.isSubmitting}>
          {t("common:save")}
        </Button>
      </SheetFooter>
    </>
  );
}

function PlayerLateralitygForm({ player, closeModal }: { player: RouterOutputs["player"]["getPlayer"]; closeModal: () => void }) {
  const { t } = useTranslation(["common", "profile"]);
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  const { mutate, isPending } = useMutation(trpc.player.updateSpecs.mutationOptions({
    onSuccess: () => {
      queryClient.invalidateQueries(trpc.player.getPlayer.queryFilter());
      closeModal();
    },
  }));

  const playerLateralityStyleForm = useForm<UpdatePlayerLateralitySchema>({
    resolver: zodResolver(updatePlayerLateralitySchema),
    defaultValues: {
      laterality: player?.laterality || undefined,
    },
  });

  const onSubmit = playerLateralityStyleForm.handleSubmit((data) => {
    mutate({ ...data, userId: player.userId });
  });

  return (
    <>
      <SheetForm id="PlayerLateralityStyleForm" onSubmit={onSubmit}>
        <Controller
          control={playerLateralityStyleForm.control}
          name="laterality"
          render={({ field, fieldState }) => (
            <RootField>
              <Label>{t("profile:laterality")}</Label>
              <Select value={field.value} onValueChange={field.onChange}>
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    {playerLaterality.map(laterality => (
                      <SelectItem key={laterality} value={laterality}>{t(`profile:${laterality}`)}</SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
              {fieldState.error?.message && (
                <ErrorField>{tDyn(t, fieldState.error?.message)}</ErrorField>
              )}
            </RootField>
          )}
        />
      </SheetForm>

      <SheetFooter>
        <SheetClose asChild>
          <Button variant="dark" type="button">
            {t("common:cancel")}
          </Button>
        </SheetClose>
        <Button type="submit" form="PlayerLateralityStyleForm" loading={isPending || playerLateralityStyleForm.formState.isSubmitting}>
          {t("common:save")}
        </Button>
      </SheetFooter>
    </>
  );
}

function PlayerPreferredSurfaceForm({ player, closeModal }: { player: RouterOutputs["player"]["getPlayer"]; closeModal: () => void }) {
  const { t } = useTranslation(["common", "profile"]);
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  const { mutate, isPending } = useMutation(trpc.player.updateSpecs.mutationOptions({
    onSuccess: () => {
      queryClient.invalidateQueries(trpc.player.getPlayer.queryFilter());
      closeModal();
    },
  }));

  const playerPreferredSurfaceForm = useForm<UpdatePlayerPreferredSurfaceSchema>({
    resolver: zodResolver(updatePlayerPreferredSurfaceSchema),
    defaultValues: {
      preferredSurface: player?.preferredSurface || undefined,
    },
  });

  const onSubmit = playerPreferredSurfaceForm.handleSubmit((data) => {
    mutate({ ...data, userId: player.userId });
  });

  return (
    <>
      <SheetForm id="PlayerPreferredSurfaceForm" onSubmit={onSubmit}>
        <Controller
          control={playerPreferredSurfaceForm.control}
          name="preferredSurface"
          render={({ field, fieldState }) => (
            <RootField>
              <Label>{t("profile:preferred_surface")}</Label>
              <Select value={field.value} onValueChange={field.onChange}>
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    {playerPreferredSurface.map(surface => (
                      <SelectItem key={surface} value={surface}>{t(`profile:${surface}`)}</SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
              {fieldState.error?.message && (
                <ErrorField>{tDyn(t, fieldState.error?.message)}</ErrorField>
              )}
            </RootField>
          )}
        />
      </SheetForm>

      <SheetFooter>
        <SheetClose asChild>
          <Button variant="dark" type="button">
            {t("common:cancel")}
          </Button>
        </SheetClose>
        <Button type="submit" form="PlayerPreferredSurfaceForm" loading={isPending || playerPreferredSurfaceForm.formState.isSubmitting}>
          {t("common:save")}
        </Button>
      </SheetFooter>
    </>
  );
}

function PlayerPreferredPriceForm({ player, closeModal }: { player: RouterOutputs["player"]["getPlayer"]; closeModal: () => void }) {
  const { t } = useTranslation(["common", "profile"]);
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  const { mutate, isPending } = useMutation(trpc.player.updateSpecs.mutationOptions({
    onSuccess: () => {
      queryClient.invalidateQueries(trpc.player.getPlayer.queryFilter());
      closeModal();
    },
  }));

  const playerPreferredPriceForm = useForm<UpdatePlayerPreferredPriceSchema>({
    resolver: zodResolver(updatePlayerPreferredPriceSchema),
    defaultValues: {
      preferredPrice: player?.preferredPrice || undefined,
    },
  });

  const onSubmit = playerPreferredPriceForm.handleSubmit((data) => {
    mutate({ ...data, userId: player.userId });
  });

  return (
    <>
      <SheetForm id="PlayerPreferredPriceForm" onSubmit={onSubmit}>
        <TextField
          type="number"
          label={t("profile:preferred_price")}
          prefix="€"
          min={0}
          {...playerPreferredPriceForm.register("preferredPrice")}
          errorMessage={tDyn(t, playerPreferredPriceForm.formState.errors.preferredPrice?.message)}
        />
      </SheetForm>

      <SheetFooter>
        <SheetClose asChild>
          <Button variant="dark" type="button">
            {t("common:cancel")}
          </Button>
        </SheetClose>
        <Button type="submit" form="PlayerPreferredPriceForm" loading={isPending || playerPreferredPriceForm.formState.isSubmitting}>
          {t("common:save")}
        </Button>
      </SheetFooter>
    </>
  );
}
